import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { readPageCache, writePageCache } from '@/lib/pageCache';
import useRefreshOnFocus from '@/lib/useRefreshOnFocus';
import '@/pages/Products.css';
import '@/pages/Clients.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const BudgetsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedBudgets = readPageCache('budgets-pending');
  const [budgets, setBudgets] = useState(cachedBudgets || []);
  const [loading, setLoading] = useState(!cachedBudgets);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [duplicatingId, setDuplicatingId] = useState('');
  const [confirmBudget, setConfirmBudget] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteBudget, setDeleteBudget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const { data } = await api.get('/budgets?status=pending');
      setBudgets(data);
      writePageCache('budgets-pending', data);
    } catch (err) {
      setError('Error al cargar presupuestos. Si la sesion vencio, volve a iniciar sesion.');
      setBudgets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshOnFocus(load);

  const handleDelete = async () => {
    if (!deleteBudget) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/budgets/${deleteBudget.id}`);
      setDeleteBudget(null);
      load();
    } catch (err) {
      alert('Error al eliminar presupuesto');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleApprove = async (b) => {
    setConfirmBudget(b);
  };

  const confirmApprove = async () => {
    if (!confirmBudget) return;
    try {
      setConfirmLoading(true);
      await api.put(`/budgets/${confirmBudget.id}/status`, { status: 'approved' });
      setConfirmBudget(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al aprobar presupuesto');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDuplicate = async (b) => {
    try {
      setDuplicatingId(b.id);
      const { data } = await api.post(`/budgets/${b.id}/duplicate`);
      navigate(`/presupuestos/${data.id}/editar`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al duplicar presupuesto');
    } finally {
      setDuplicatingId('');
    }
  };

  const filteredBudgets = budgets.filter((budget) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    const clientName = budget.client_snapshot?.name || '';
    const productName = budget.product_snapshot?.name || '';
    const number = String(budget.budget_number || '');
    return (
      clientName.toLowerCase().includes(query)
      || productName.toLowerCase().includes(query)
      || number.includes(query)
    );
  });

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="budgets-title">Presupuestos</h1>
            <p className="dashboard-subtitle">
              Presupuestos enviados al cliente y pendientes de aprobacion
            </p>
          </div>
          <button
            className="btn-primary-inline"
            onClick={() => navigate('/presupuestos/nuevo')}
            data-testid="new-budget-button"
          >
            + Nuevo Presupuesto
          </button>
        </div>

        {error && <div className="login-error" data-testid="budgets-error">{error}</div>}

        <div className="card approved-search-card">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cliente, producto o numero"
              data-testid="budgets-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="empty-state">Cargando presupuestos...</div>
        ) : budgets.length === 0 ? (
          <div className="card">
            <div className="empty-state" data-testid="empty-budgets">
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No hay presupuestos pendientes</p>
              <p style={{ fontSize: '0.875rem', color: '#A0A0A0' }}>
                Necesitás al menos un cliente y un producto para crear un presupuesto
              </p>
            </div>
          </div>
        ) : filteredBudgets.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              No hay presupuestos que coincidan con la busqueda.
            </div>
          </div>
        ) : (
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>N°</th>
                  <th>Cliente</th>
                  <th>Producto</th>
                  <th>Fecha</th>
                  <th style={{ textAlign: 'right' }}>Precio Final</th>
                  <th style={{ width: '320px', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredBudgets.map(b => (
                  <tr key={b.id} data-testid={`budget-row-${b.id}`}>
                    <td className="cell-strong mono">#{String(b.budget_number).padStart(4, '0')}</td>
                    <td>{b.client_snapshot?.name || '-'}</td>
                    <td>{b.product_snapshot?.name || '-'}</td>
                    <td>{formatDate(b.created_at)}</td>
                    <td style={{ textAlign: 'right' }} className="mono cell-strong">{formatMoney(b.final_price)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Link
                        to={`/presupuestos/${b.id}`}
                        className="btn-link"
                        data-testid={`view-budget-${b.id}`}
                      >
                        Ver
                      </Link>
                      <Link
                        to={`/presupuestos/${b.id}/editar`}
                        className="btn-link"
                        data-testid={`edit-budget-${b.id}`}
                      >
                        Editar
                      </Link>
                      <button
                        className="btn-link"
                        onClick={() => handleDuplicate(b)}
                        disabled={duplicatingId === b.id}
                        data-testid={`duplicate-budget-${b.id}`}
                      >
                        {duplicatingId === b.id ? 'Duplicando...' : 'Duplicar'}
                      </button>
                      <button
                        className="btn-link"
                        onClick={() => handleApprove(b)}
                        data-testid={`approve-budget-${b.id}`}
                      >
                        Aprobar
                      </button>
                      <button
                        className="btn-link btn-link-danger"
                        onClick={() => setDeleteBudget(b)}
                        data-testid={`delete-budget-${b.id}`}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ConfirmDialog
          open={!!confirmBudget}
          title="Aprobar presupuesto"
          message={confirmBudget ? `¿Estas seguro de aprobar el presupuesto #${confirmBudget.budget_number}?` : ''}
          confirmLabel="Aprobar"
          loading={confirmLoading}
          onConfirm={confirmApprove}
          onCancel={() => setConfirmBudget(null)}
        />
        <ConfirmDialog
          open={!!deleteBudget}
          title="Eliminar presupuesto"
          message={deleteBudget ? `¿Estas seguro de eliminar el presupuesto #${deleteBudget.budget_number}?` : ''}
          confirmLabel="Eliminar"
          loading={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setDeleteBudget(null)}
        />
      </main>
    </div>
  );
};

export default BudgetsList;
